import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CommunicationChannels from './CommunicationChannels';
import UseCases from './UseCases';
import PersonalityConfig from './PersonalityConfig';
import { useEffect } from 'react';
import ConversationFlowDesigner from './ConversationFlowDesigner';
import OnboardingButtonDock from '../../components/OnboardingButtonDock';
import { ReactFlowProvider } from 'reactflow';
import AgentPlayground from './AgentPlayground';
import Setup from './SetupCRM';
import FinishAgentSetup from './FinishAgentSetup';
import axios from 'axios';

axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL

const steps = [
  { name: 'Communication Channels', description: 'Select how users will interact with your AI agent' },
  { name: 'Use Cases', description: 'Choose the primary purpose of your AI agent' },
  { name: 'Personality', description: 'Customize how your AI agent responds' },
  { name: 'Conversation Flow', description: 'Design your conversation flow' },
  { name: 'Playground', description: 'Interact with your AI agent in real-time' },
  { name: 'Finish', description: 'Review and save your AI agent configuration' }
];

// Utility to get userId from localStorage (same as Dashboards.jsx and ProtectedRoute.js)
const getUserId = () => localStorage.getItem('userId');

const CreateAgent = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [selectedUseCase, setSelectedUseCase] = useState('');
  const [selectedPersonality, setSelectedPersonality] = useState('Professional'); 
  const [conversationFlow, setConversationFlow] = useState(null);
  const [agentId, setAgentId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [personality, setPersonality] = useState({
    name: '',
    greeting: '',
    description: '',
    personalityType: 'Professional',
    voice: {
      language: 'English',
      name: 'shweta',
      speed: 1,
      pitch: 0
    },
  });

  const handleNext = () => {
    setActiveStep((prevStep) => Math.min(prevStep + 1, steps.length - 1));
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate('/ai-agents');
    } else {
      setActiveStep((prevStep) => Math.max(prevStep - 1, 0));
    }
  };

  const handleConversationFlowSave = (flow) => {
    setConversationFlow(flow);
  };

  // Auto-advance and save on channel select
  const handleChannelToggle = async (channelName) => {
    setSelectedChannel(channelName);
    setSaving(true);
    setError(null);
    try {
      const userId = getUserId();
      if (!userId) {
        setError('User not logged in.');
        setSaving(false);
        return;
      }
      if (!agentId) {
        const response = await axios.post('/api/ai-agents', { channel: channelName, userId });
        if (response.data && response.data.agentId) {
          setAgentId(response.data.agentId);
        } else {
          setError('Agent creation failed: No agentId returned.');
          return;
        }
      }
      setActiveStep(1); // Move to use case step
    } catch (err) {
      setError('Failed to save channel.');
    } finally {
      setSaving(false);
    }
  };

  // Auto-advance and save on use case select
  const handleUseCaseSelect = async (useCase) => {
    setSelectedUseCase(useCase);
    if (!agentId) {
      setError('Agent must be created before saving use case. Please complete the previous step.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const userId = getUserId();
      if (!userId) {
        setError('User not logged in.');
        setSaving(false);
        return;
      }
      await axios.put(`/api/ai-agents/${agentId}/use-case`, { useCase, userId });
      setActiveStep(2); // Move to personality step
    } catch (err) {
      setError('Failed to save use case.');
    } finally {
      setSaving(false);
    }
  };

  const handlePersonalitySelect = (personality) => {
    setSelectedPersonality(personality);
  };

  // Step 1: Save Channels
  const handleSaveChannels = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!agentId) {
        const response = await axios.post('/api/ai-agents', { channel: selectedChannel });
        if (response.data && response.data.agentId) {
          setAgentId(response.data.agentId);
        } else {
          setError('Agent creation failed: No agentId returned.');
          return;
        }
      }
    } catch (err) {
      setError('Failed to save channel.');
    } finally {
      setSaving(false);
    }
  };

  // Step 2: Save Use Case
  const handleSaveUseCase = async () => {
    console.log('handleSaveUseCase called');
    if (!agentId) {
      setError('Agent must be created before saving use case. Please complete the previous step.');
      console.error('No agentId set!');
      return;
    }
    setSaving(true);
    setError(null);
    console.log('Saving use case:', selectedUseCase, 'for agent:', agentId);
    try {
      const userId = getUserId();
      if (!userId) {
        setError('User not logged in.');
        setSaving(false);
        return;
      }
      await axios.put(`/api/ai-agents/${agentId}/use-case`, { useCase: selectedUseCase, userId });
      console.log('Use case saved.');
    } catch (err) {
      setError('Failed to save use case.');
      console.error('Error saving use case:', err);
    } finally {
      setSaving(false);
    }
  };

  // Step 3: Save Personality
  const handleSavePersonality = async () => {
    console.log('handleSavePersonality called with agentId:', agentId, 'personality:', personality);
    
    if (!agentId) {
      setError('Agent must be created before saving personality. Please complete the previous steps.');
      return;
    }
    
    setSaving(true);
    setError(null);
    try {
      const userId = getUserId();
      if (!userId) {
        setError('User not logged in.');
        setSaving(false);
        return;
      }
      
      console.log('Saving personality for agent:', agentId, 'with data:', { personality, userId });
      
      const response = await axios.put(`/api/ai-agents/${agentId}/personality`, {
        personality,
        userId,
      });
      
      console.log('Personality save response:', response);
      
      if (response.status === 200) {
        console.log('Personality saved successfully, moving to step 3');
        setActiveStep(3); // Move to conversation flow step
      } else {
        throw new Error('Failed to save personality configuration');
      }
    } catch (err) {
      console.error('Error saving personality:', err);
      setError(err.response?.data?.error || 'Failed to save personality configuration.');
    } finally {
      setSaving(false);
    }
  };

  // Back handler for step 3 (update if needed, then go back)
  const handleBackPersonality = async () => {
    if (!agentId) {
      setActiveStep(1);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const userId = getUserId();
      await axios.put(`/api/ai-agents/${agentId}/personality`, {
        personality,
        userId,
      });
      setActiveStep(1); // Go back to use case step
    } catch (err) {
      setError('Failed to update personality.');
    } finally {
      setSaving(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <CommunicationChannels
            selectedChannel={selectedChannel}
            onChannelToggle={handleChannelToggle}
          />
        );
      case 1:
        return (
          <UseCases
            selectedUseCase={selectedUseCase}
            onUseCaseSelect={handleUseCaseSelect}
          />
        );
      case 2:
        return (
          <PersonalityConfig
            personality={personality}
            setPersonality={setPersonality}
            agentId={agentId}
            onSave={handleSavePersonality}
            onBack={handleBackPersonality}
          />
        );
      case 3:
        return (
          <div className="h-[calc(100vh)]">
            <ReactFlowProvider>
              <ConversationFlowDesigner
                  onSave={handleConversationFlowSave}
                  initialFlow={conversationFlow}
              />
            </ReactFlowProvider>
          </div>
        );
      case 4:
        return (
          <div className="h-[calc(100vh)]">
            <AgentPlayground />
          </div>
        );
      case 5:
        return (
          <div className="h-[calc(100vh)]">
            <FinishAgentSetup
              agentDetails={{
                communicationChannels: selectedChannel,
                useCase: selectedUseCase,
                personality: selectedPersonality,
                conversationFlow: conversationFlow ? 'Configured' : 'Not Configured',
              }}
            />
          </div>
        );
      default:
        return <p className="text-xl">Step {activeStep + 1}: {steps[activeStep].name}</p>;
    }
  };

  const handleStepSave = async () => {
    if (activeStep === 0) {
       await handleSaveChannels();
      setActiveStep(1);
    } else if (activeStep === 1) {
      await handleSaveUseCase();
      setActiveStep(2);
    } else if (activeStep === 2) {
      await handleSavePersonality();
      setActiveStep(3)
    }else if (activeStep===3){
      await handleConversationFlowSave();
      setActiveStep(4)
    }
  };

  const handleStepBack = async () => {
    if (activeStep === 0) {
      navigate('/ai-agents');
    } else if (activeStep === 1) {
      setActiveStep(0);
    } else if (activeStep === 2) {
      await handleBackPersonality();
    } else {
      setActiveStep((prevStep) => Math.max(prevStep - 1, 0));
    }
  };

  return (
    <div className="pt-5 w-screen mx-auto">
      <div className="">
        {/* Only show the stepper if activeStep > 0 */}
        {activeStep > 0 && (
        <div className="flex items-center justify-between w-screen mb-10 px-20">
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <motion.div
                className="relative"
                initial={false}
                animate={{ scale: activeStep === index ? 1.1 : 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              >
                <motion.div
                  className={`flex items-center justify-center w-7 h-7 rounded-full border-2 transition-colors duration-300 ${
                    index < activeStep
                      ? "bg-blue-600 border-blue-600 text-white"
                      : index === activeStep
                      ? "border-blue-600 text-blue-600"
                      : "border-gray-300 text-gray-400"
                  }`}
                  whileHover={index <= activeStep ? { scale: 1.05 } : {}}
                  onClick={() => index < activeStep && setActiveStep(index)}
                  style={{ cursor: index < activeStep ? "pointer" : "default" }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Step ${index + 1}: ${step.name}`}
                  aria-current={index === activeStep ? "step" : undefined}
                >
                  {index < activeStep ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="text-sm font-medium">{index + 1}</span>
                  )}
                </motion.div>
                <div className="absolute mt-2 top-full left-1/2 transform -translate-x-1/2 w-max">
                  <p className={`text-xs font-medium whitespace-nowrap ${
                    index <= activeStep ? "text-blue-600" : "text-gray-500"
                  }`}>
                    {step.name}
                  </p>
                </div>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="bg-gray-800 text-white text-xs rounded py-1 px-2 pointer-events-none">
                    {step.description}
                  </div>
                </div>
              </motion.div>
              {index < steps.length - 1 && (
                <div className="flex-1 mx-4">
                  <div className="h-0.5 relative">
                    <div className="absolute inset-0 bg-gray-200"></div>
                    <motion.div
                      className="absolute inset-0 bg-blue-600"
                      initial={{ width: index < activeStep ? "100%" : "0%" }}
                      animate={{ width: index < activeStep ? "100%" : "0%" }}
                      transition={{ duration: 0.5 }}
                    ></motion.div>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
          </div>
        )}
        </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={activeStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className=" rounded-lg shadow-lg mx-5  mb-4 "
        >
          {renderStepContent()}
        </motion.div>
      </AnimatePresence>
      {console.log('activeStep:', activeStep)}
      <OnboardingButtonDock
        onSave={handleStepSave}
        onNext={handleStepSave}
        onBack={handleStepBack}
        title={
          activeStep === 0
            ? 'Save Channels'
            : activeStep === 1
            ? 'Save Use Case'
            : activeStep === 2
            ? 'Save Personality'
            : activeStep === steps.length - 1
            ? 'Finish'
            : `Save ${steps[activeStep].name}`
        }
        disabled={
          (activeStep === 0 && !selectedChannel) ||
          (activeStep === 1 && !selectedUseCase) ||
          (activeStep === 2 && (!personality.name || !personality.personalityType))
        }
        desktopClassName="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50"
        mobileClassName="fixed bottom-8 right-8 z-50"
      />
    </div>
  );
};

export default CreateAgent;
